import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { message } from 'antd';
import { DashboardFilters, DashboardCard, PendingItem, CardColor, Priority } from './types';
import {
  DEFAULT_DATE_RANGE_DAYS,
  FALLBACK_CARDS,
  FALLBACK_PENDING_ITEMS,
} from './constants';
import { dashboardService, DashboardStats } from '@/services/dashboard.service';
import { useAuth } from '@/contexts/auth.context';
import { usePermissions } from '@/shared/hooks/usePermissions';

function createDefaultFilters(): DashboardFilters {
  return {
    startDate: dayjs().subtract(DEFAULT_DATE_RANGE_DAYS, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  };
}

function mapApiCardToCard(apiCard: DashboardStats['cards'][0]): DashboardCard {
  return {
    id: apiCard.id,
    title: apiCard.title,
    icon: apiCard.icon,
    total: apiCard.total,
    color: apiCard.color as CardColor,
    countsByDate: apiCard.countsByDate.map(c => ({
      label: c.label,
      count: c.count,
      isOld: c.isOld,
    })),
    subItems: apiCard.subItems?.map(s => ({
      label: s.label,
      count: s.count,
    })),
  };
}

function mapApiPendingItemToPendingItem(apiItem: DashboardStats['pendingItems'][0]): PendingItem {
  return {
    id: apiItem.id,
    inspectionItemId: apiItem.inspectionItemId,
    code: apiItem.code,
    type: apiItem.type,
    description: apiItem.description,
    daysOverdue: apiItem.daysOverdue,
    priority: apiItem.priority as Priority,
  };
}

function buildCardDetailUrl(cardId: string, filters: DashboardFilters): string {
  const params = new URLSearchParams();
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  return `/dashboard/${cardId}?${params.toString()}`;
}

function validateFilters(filters: DashboardFilters): string | null {
  if (!filters.startDate || !filters.endDate) {
    return 'O período de datas é obrigatório';
  }
  if (dayjs(filters.startDate).isAfter(dayjs(filters.endDate))) {
    return 'A data inicial não pode ser maior que a data final';
  }
  return null;
}

export function useDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isProviderUser } = usePermissions();
  const isProvider = isProviderUser();
  const providerId = user?.providerId;
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<DashboardFilters>(createDefaultFilters);
  const [cards, setCards] = useState<DashboardCard[]>(FALLBACK_CARDS);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>(FALLBACK_PENDING_ITEMS);

  const loadDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiFilters: DashboardFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
      };

      if (isProvider && providerId) {
        apiFilters.providerId = providerId;
      }

      const response = await dashboardService.getStats(apiFilters);

      setCards(response.cards.map(mapApiCardToCard));
      setPendingItems(response.pendingItems.map(mapApiPendingItemToPendingItem));
    } catch {
      // Use fallback data on error
    } finally {
      setIsLoading(false);
    }
  }, [filters.startDate, filters.endDate, isProvider, providerId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleFilterChange = useCallback((field: keyof DashboardFilters, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(createDefaultFilters());
    message.info('Filtros limpos');
  }, []);

  const handleApplyFilters = useCallback(async () => {
    const validationError = validateFilters(filters);
    if (validationError) {
      message.error(validationError);
      return;
    }

    await loadDashboardData();
    message.success('Filtros aplicados com sucesso');
  }, [filters, loadDashboardData]);

  const handleCardClick = useCallback((cardId: string) => {
    navigate(buildCardDetailUrl(cardId, filters));
  }, [navigate, filters]);

  const handlePendingItemClick = useCallback((itemId: string) => {
    navigate(`/dashboard/all_inspections?inspectionId=${itemId}`);
  }, [navigate]);

  const totalInspections = useMemo(() => {
    return cards.reduce((sum, card) => sum + card.total, 0);
  }, [cards]);

  return {
    filters,
    cards,
    pendingItems,
    isLoading,
    totalInspections,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
    handleCardClick,
    handlePendingItemClick,
  };
}
